import { MissionJobRead, OdysseyRead } from 'api/schema'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import { IMission } from 'utils/interfaces'

import style from './FacilityCleaningTaskItem.module.css'

type CombinedTask = (MissionJobRead | OdysseyRead) & { isOdyssey?: boolean; name?: string }

export interface IFacilityCleaningTaskItemProps {
  task: CombinedTask
  onClick?: () => void
  highlighted?: boolean
  showCheckbox?: boolean
  checked?: boolean
  onCheckboxChange?: (checked: boolean) => void
  scheduledMissionsDetails?: IMission[]
  referencedScenarios?: any[]
  onLaunch?: (task: CombinedTask) => void
  onReschedule?: (task: CombinedTask) => void
  isOverlapping?: boolean
}

const FacilityCleaningTaskItem = ({
  task,
  onClick,
  highlighted,
  showCheckbox,
  checked,
  onCheckboxChange,
  scheduledMissionsDetails,
  referencedScenarios,
  onLaunch,
  onReschedule,
  isOverlapping,
}: IFacilityCleaningTaskItemProps) => {
  const navigate = useNavigate()

  const getMissions = () => {
    if (task.isOdyssey) return (task as OdysseyRead).missions || []
    if ('missions' in task && Array.isArray((task as any).missions)) return (task as any).missions
    const mId = (task as MissionJobRead).mission_id
    return mId ? [mId] : []
  }

  const getDisplayName = () => {
    if (task.name) return task.name
    return getMissions().join(', ') || '-'
  }

  const missionIds = getMissions()
  const displayName = getDisplayName()

  const isScenarioInconsistent =
    task.isOdyssey &&
    (task as OdysseyRead).scenario &&
    referencedScenarios?.find(s => s.id === (task as OdysseyRead).scenario)?.missions_type_inconsistent

  const hasUncalculatedMissions = missionIds.some((mId: number) => {
    const details = scheduledMissionsDetails?.find(m => m.mission.id === mId)
    return details && details.status !== 'ok'
  })

  const isLaunchDisabled = missionIds.length === 0 || isScenarioInconsistent || hasUncalculatedMissions

  const getSingleScheduleString = (item: CombinedTask) => {
    let schedule = ''
    const days = item.scheduler_settings?.weekdays
    if (days) {
      if (days.SU === 'true') schedule += 'SU '
      if (days.MO === 'true') schedule += 'MO '
      if (days.TU === 'true') schedule += 'TU '
      if (days.WE === 'true') schedule += 'WE '
      if (days.TH === 'true') schedule += 'TH '
      if (days.FR === 'true') schedule += 'FR '
      if (days.SA === 'true') schedule += 'SA '
    }

    if (item.scheduler_settings?.datetime) {
      const dt = new Date(item.scheduler_settings.datetime.replace(' ', 'T'))
      const timeStr = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      schedule += '\n' + timeStr

      const stats = (item as any).average_statistics
      if (stats?.duration_overall) {
        const endDt = new Date(dt.getTime() + stats.duration_overall * 1000)
        const endTimeStr = endDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        schedule += ' - ' + endTimeStr
      } else {
        const execution_time = (item as any).execution_time
        if (execution_time) {
          const endDt = new Date(dt.getTime() + execution_time * 1000)
          const endTimeStr = endDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          schedule += ' - ' + endTimeStr
        }
      }
    }

    return schedule.trim() || '-'
  }

  return (
    <div
      className={clsx(style.taskRow, {
        [style.highlighted]: highlighted,
        [style.errorRow]: isLaunchDisabled,
        [style.overlappingRow]: isOverlapping,
      })}
      title={isOverlapping ? 'This task overlaps with another schedule for the same robot' : undefined}
      onClick={e => {
        e.stopPropagation()
        onClick?.()
      }}
    >
      <div></div>
      <span className={style.colLeft} title={task.name || String(task.id)}>
        {task.id}
      </span>
      <span className={style.colMultiline} title={displayName}>
        {displayName}
      </span>
      <span className={style.colSchedule} title={getSingleScheduleString(task)}>
        {getSingleScheduleString(task)}
      </span>
      <span className={style.colCenter} onClick={showCheckbox ? e => e.stopPropagation() : undefined}>
        {showCheckbox && (
          <input type="checkbox" checked={checked} onChange={e => onCheckboxChange?.(e.target.checked)} />
        )}
      </span>
      <span className={style.colCenter}>
        {missionIds.length > 0 && (
          <ActionsMenu>
            <ActionsMenuItem onClick={() => onLaunch?.(task)} disabled={isLaunchDisabled}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Launch</span>
            </ActionsMenuItem>
            <ActionsMenuItem
              onClick={() => {
                navigate(`${PATH.DASHBOARD}?taskId=${task.id}`)
              }}
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Dashboard</span>
            </ActionsMenuItem>
            <ActionsMenuItem
              onClick={() => {
                const path =
                  task.isOdyssey && (task as OdysseyRead).scenario
                    ? `${PATH.SCENARIO_ID.replace(':id', String((task as OdysseyRead).scenario))}`
                    : `${PATH.MISSION}${missionIds[0]}`
                navigate(path)
              }}
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Edit</span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => onReschedule?.(task)}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Reschedule</span>
            </ActionsMenuItem>
          </ActionsMenu>
        )}
      </span>
    </div>
  )
}

export default FacilityCleaningTaskItem
